import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import alphalogo from '../../../utils/alphahorizontal.png';

/*************************************************
 * Dynamic Style Factory – matches MtoInfraPDF    *
 *************************************************/
const createStyles = (primaryColor = '#005695', secondaryColor = '#0067b2') => {
  const colors = {
    primary: primaryColor,
    secondary: secondaryColor,
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      800: '#1F2937',
    },
    white: '#FFFFFF',
  };

  return StyleSheet.create({
    /* ===== Page ===== */
    page: {
      backgroundColor: colors.white,
      padding: 30,
      paddingBottom: 30,
      fontFamily: 'Helvetica',
    },

    /* ===== Header ===== */
    header: {
      backgroundColor: colors.white,
      padding: 15,
      borderRadius: 8,
      marginBottom: 10,
      borderWidth: 1,
      borderColor: colors.gray[200],
    },
    headerContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 20,
    },
    headerLogoContainer: {
      backgroundColor: colors.white,
      padding: 8,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.gray[200],
      width: '20%',
    },
    headerLogo: {
      width: '100%',
      height: 40,
      objectFit: 'contain',
    },
    headerTitle: {
      backgroundColor: colors.primary,
      padding: '8 12',
      borderRadius: 6,
      width: '60%',
    },
    headerTitleText: {
      fontSize: 11,
      color: colors.white,
      fontWeight: 'bold',
      textAlign: 'center',
    },
    headerInfo: {
      width: '25%',
      gap: 6,
    },
    headerInfoItem: {
      backgroundColor: colors.gray[50],
      padding: '6 10',
      borderRadius: 4,
      marginBottom: 4,
    },
    value: {
      fontSize: 8,
      color: colors.gray[800],
    },

    /* ===== Generic Sections ===== */
    section: {
      backgroundColor: colors.white,
      padding: 8,
      borderRadius: 8,
      marginBottom: 10,
      borderWidth: 1,
      borderColor: colors.gray[200],
    },
    sectionTitle: {
      backgroundColor: colors.primary,
      padding: '6 10',
      borderRadius: 4,
      marginBottom: 6,
    },
    sectionTitleText: {
      fontSize: 10,
      color: colors.white,
      fontWeight: 'bold',
    },

    /* ===== Two‑column container ===== */
    twoCol: {
      flexDirection: 'row',
      gap: 10,
    },
    col: {
      flex: 1,
    },

    /* ===== Rows & text ===== */
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 5,
      gap: 8,
    },
    label: {
      fontSize: 8,
      color: colors.gray[800],
      fontWeight: 'bold',
      width: '40%',
    },
    text: {
      fontSize: 8,
      color: colors.gray[800],
      flex: 1,
    },

    longTextBox: {
      backgroundColor: colors.gray[50],
      padding: 8,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      minHeight: 40,
    },
    longText: {
      fontSize: 8,
      color: colors.gray[800],
      lineHeight: 1.4,
    },

    /* ===== Prestador logo ===== */
    prestadorLogoContainer: { alignItems: 'center', marginBottom: 8 },
    prestadorLogo: {
      width: 100,
      height: 80,
      objectFit: 'contain',
      borderWidth: 1,
      borderColor: colors.gray[200],
      borderRadius: 6,
      backgroundColor: colors.gray[50],
    },

    /* ===== Materials table ===== */
    materialsHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      padding: '6 8',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      marginBottom: 2,
      alignItems: 'center',
    },
    materialsHeaderText: {
      fontSize: 8,
      color: colors.white,
      fontWeight: 'bold',
      width: '25%',
      textAlign: 'center',
    },
    materialRow: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.gray[50],
      padding: '6 8',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      marginBottom: 2,
    },
    materialCell: {
      fontSize: 8,
      color: colors.gray[800],
      width: '25%',
      textAlign: 'center',
    },
    materialTotalRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      backgroundColor: colors.gray[50],
      padding: '6 8',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      marginTop: 4,
    },
    materialTotalLabel: {
      fontSize: 8,
      color: colors.gray[800],
      fontWeight: 'bold',
      marginRight: 8,
    },
    materialTotalValue: {
      fontSize: 8,
      color: colors.gray[800],
      fontWeight: 'bold',
    },

    /* ===== Personnel ===== */
    personnelContainer: { flexDirection: 'row', gap: 4, marginTop: 6 },
    personnelHalf: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.white,
      padding: 2,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
      gap: 6,
    },
    signatureContainer: {
      width: '40%',
      height: 60,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.gray[50],
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.gray[200],
    },
    signatureImage: { width: '90%', height: '90%', objectFit: 'contain' },
    noSignatureText: { fontSize: 8, color: colors.gray[800], fontStyle: 'italic', textAlign: 'center' },
    personnelInfo: { flex: 1, gap: 2 },
    labelPersonnel: { fontSize: 10, color: colors.gray[800], fontWeight: 'bold', textAlign: 'center' },
    roleText: { fontSize: 9, color: colors.primary, textAlign: 'center' },

    /* ===== Footer ===== */
    footer: {
      position: 'absolute',
      bottom: 20,
      left: 30,
      right: 30,
      borderTopWidth: 1,
      borderColor: colors.gray[200],
      paddingTop: 5,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    footerLogo: { width: 120, height: 24, objectFit: 'contain' },
    footerWeb: { fontSize: 9, color: colors.primary, fontWeight: 'bold' },
  });
};

/*********************************************************************
 * Reporte de Mantenimiento Correctivo de Infraestructura – 1 página
 *********************************************************************/
const ReporteMtoCorrInfraPDF = ({ area, cliente, mto, solicitud }) => {
  if (!area || !cliente || !mto || !solicitud) {
    return (
      <Document>
        <Page size="A4">
          <View>
            <Text>No hay información disponible</Text>
          </View>
        </Page>
      </Document>
    );
  }

  const styles = createStyles(cliente.primaryColor, cliente.secondaryColor);
  const logoPrestador = cliente.imagenurl || 'https://firebasestorage.googleapis.com/v0/b/alphaplatform.appspot.com/o/noImg.png?alt=media';

  const renderMaterialRow = (mat, qty, cost, sub, key) => (
    <View style={styles.materialRow} key={key}>
      <Text style={styles.materialCell}>{mat || 'No Registra'}</Text>
      <Text style={styles.materialCell}>{qty || '0'}</Text>
      <Text style={styles.materialCell}>{cost || '0'}</Text>
      <Text style={styles.materialCell}>{sub || '0'}</Text>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* ===== HEADER ===== */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLogoContainer}>
              <Image src={logoPrestador} style={styles.headerLogo} />
            </View>
            <View style={styles.headerTitle}>
              <Text style={styles.headerTitleText}>REPORTE DE MANTENIMIENTO CORRECTIVO DE INFRAESTRUCTURA</Text>
            </View>
            <View style={styles.headerInfo}>
              <View style={styles.headerInfoItem}>
                <Text style={styles.value}>Reporte No: {(mto.mtocorrinfraid|| '').toString().slice(28, 36) || 'N/A'}</Text>
              </View>
              <View style={styles.headerInfoItem}>
                <Text style={styles.value}>Fecha: {mto.fechainicio || 'N/A'}</Text>
              </View>
            </View>
          </View>
        </View>

     {/* ===== SECCIÓN ÁREA DE INTERVENCIÓN Y PRESTADOR ===== */}
<View style={[styles.section, styles.twoCol]} wrap={false}>
  {/* Columna izquierda: área con imagen + datos al lado */}
  <View style={styles.col}>
    <View style={styles.sectionTitle}>
      <Text style={styles.sectionTitleText}>ÁREA DE INTERVENCIÓN</Text>
    </View>
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      {/* Imagen del área */}
      <Image
        src={area.imagenurl || 'https://firebasestorage.googleapis.com/v0/b/alphaplatform.appspot.com/o/noImg.png?alt=media'}
        style={{
          width: 60,
          height: 50,
          objectFit: 'cover',
          borderRadius: 4,
          borderWidth: 1,
          borderColor: '#E5E7EB',
        }}
      />

      {/* Info del área */}
      <View style={{ flex: 1, flexWrap: 'wrap' }}>
        {[
          ['NOMBRE', area.nombre],
          ['SERVICIO', area.servicio],
          ['UBICACIÓN', area.ubicacion],
          ['PISO', area.piso],
          ['ESTADO', area.estado],
        ].map(([l, v]) => (
          <View style={{ flexDirection: 'row', marginBottom: 2 }} key={l}>
            <Text style={{ fontSize: 8, fontWeight: 'bold', width: 50, color: '#1F2937' }}>{l}:</Text>
            <Text style={{ fontSize: 8, color: '#1F2937' }}>{v || 'N/A'}</Text>
          </View>
        ))}
      </View>
    </View>
  </View>

  {/* Columna derecha: info del prestador */}
  <View style={styles.col}>
    <View style={styles.sectionTitle}>
      <Text style={styles.sectionTitleText}>INFORMACIÓN DEL PRESTADOR</Text>
    </View>
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
      {[
        ['NOMBRE', cliente.nombre],
        ['CIUDAD', cliente.ciudad],
        ['TELÉFONO', cliente.telefono],
        ['DIRECCIÓN', cliente.direccion],
        ['SEDE', cliente.sede],
      ].map(([l, v]) => (
        <View style={{ width: '48%', marginBottom: 3 }} key={l}>
          <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#1F2937' }}>{l}:</Text>
          <Text style={{ fontSize: 8, color: '#1F2937' }}>{v || 'N/A'}</Text>
        </View>
      ))}
    </View>
  </View>
</View>

        {/* ===== SOLICITUD & CAMPOS ===== */}
        <View style={styles.twoCol} wrap={false}>
          {/* Solicitud */}
          <View style={[styles.section, styles.col]} wrap={false}>
            <View style={styles.sectionTitle}><Text style={styles.sectionTitleText}>SOLICITUD</Text></View>
            <View style={styles.row}><Text style={styles.label}>FECHA:</Text><Text style={styles.text}>{solicitud.fechasolicitud || 'N/A'}</Text></View>
            <View style={styles.row}><Text style={styles.label}>HORA:</Text><Text style={styles.text}>{solicitud.horasolicitud || 'N/A'}</Text></View>
            <View style={styles.longTextBox}><Text style={styles.longText}>{solicitud.infosolicitud || 'Sin descripción'}</Text></View>
          </View>
          {/* Campos */}
          <View style={[styles.section, styles.col]} wrap={false}>
            <View style={styles.sectionTitle}><Text style={styles.sectionTitleText}>CAMPOS DE INTERVENCIÓN</Text></View>
            {[
              ['RED ELÉCTRICA', mto.redElectrica],
              ['RED HIDRÁULICA', mto.redHidraulica],
              ['RED DE GASES', mto.redGases],
              ['INST. FÍSICAS', mto.redInstalaciones],
              ['RED COMUNICACIONES', mto.redComunicaciones],
              ['OTRA', mto.redOtros],
            ].map(([l, v]) => (
              <View style={styles.row} key={l}><Text style={styles.label}>{l}:</Text><Text style={styles.text}>{v || 'No Registra'}</Text></View>
            ))}
          </View>
        </View>

       {/* ===== ACTIVIDADES Y OBSERVACIONES ===== */}
<View style={[styles.section, styles.twoCol]} wrap={false}>
  {/* Actividades */}
  <View style={styles.col}>
    <View style={styles.sectionTitle}>
      <Text style={styles.sectionTitleText}>ACTIVIDADES REALIZADAS</Text>
    </View>
    <View style={styles.longTextBox}>
      <Text style={styles.longText}>{mto.actividades || 'Ninguna'}</Text>
    </View>
  </View>

  {/* Observaciones */}
  <View style={styles.col}>
    <View style={styles.sectionTitle}>
      <Text style={styles.sectionTitleText}>OBSERVACIONES</Text>
    </View>
    <View style={styles.longTextBox}>
      <Text style={styles.longText}>{mto.observaciones || 'Ninguna'}</Text>
    </View>
  </View>
</View>

        {/* ===== MATERIALES ===== */}
        <View style={styles.section} wrap={false}>
          
          <View style={styles.materialsHeader}>
            {['Material', 'Cantidad', 'Costo', 'Subtotal'].map(h => <Text key={h} style={styles.materialsHeaderText}>{h}</Text>)}
          </View>
          {renderMaterialRow(mto.nombrerepuesto, mto.cantidad, mto.costo, mto.subtotal, 1)}
          {renderMaterialRow(mto.nombrerepuesto1, mto.cantidad1, mto.costo1, mto.subtotal1, 2)}
          {renderMaterialRow(mto.nombrerepuesto2, mto.cantidad2, mto.costo2, mto.subtotal2, 3)}
          <View style={styles.materialTotalRow}><Text style={styles.materialTotalLabel}>TOTAL:</Text><Text style={styles.materialTotalValue}>{mto.totalrepuesto || '0'}</Text></View>
        </View>

       

      {/* ===== PERSONAL ===== */}
<View style={styles.section} wrap={false}>
  <View style={styles.sectionTitle}>
    <Text style={styles.sectionTitleText}>PERSONAL</Text>
  </View>

  {/* Fechas y horas en dos columnas */}
  <View style={styles.twoCol}>
    <View style={styles.col}>
      <View style={styles.row}>
        <Text style={styles.label}>FECHA INICIO:</Text>
        <Text style={styles.text}>{mto.fechainicio || 'N/A'}</Text>
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>HORA INICIO:</Text>
        <Text style={styles.text}>{mto.horainicio || 'N/A'}</Text>
      </View>
    </View>
    <View style={styles.col}>
      <View style={styles.row}>
        <Text style={styles.label}>FECHA FINAL:</Text>
        <Text style={styles.text}>{mto.fechafinal || 'N/A'}</Text>
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>HORA FINAL:</Text>
        <Text style={styles.text}>{mto.horafinal || 'N/A'}</Text>
      </View>
    </View>
  </View>

  {/* Firmas */}
  <View style={styles.personnelContainer}>
    {/* Recibe */}
    <View style={styles.personnelHalf}>
      <View style={styles.signatureContainer}>
        {mto.metodoRecepcion === 'manual'
          ? (cliente.imagenfirma
              ? <Image src={cliente.imagenfirma} style={styles.signatureImage} />
              : <Text style={styles.noSignatureText}>Pendiente firma</Text>)
          : (mto.imagenfirma
              ? <Image src={mto.imagenfirma} style={styles.signatureImage}
              />
              : <Text style={styles.noSignatureText}>Pendiente firma</Text>)}
      </View>
      <View style={styles.personnelInfo}>

        {mto.metodoRecepcion==='usuario'?(<>
          <Text style={styles.labelPersonnel}>{mto.datosUsuarioRecibe.nombre || ' Usuario N/A'}</Text>
          <Text style={styles.roleText}>{mto.datosUsuarioRecibe.cargo || 'Cargo/rol'}</Text>
        </>):(<>
        <Text style={styles.labelPersonnel}>{mto.firmarecibe || 'N/A'}</Text>
        <Text style={styles.roleText}>{mto.cargorecibe || 'Cargo/rol'}</Text></>)}
        
        <Text style={styles.roleText}>Aprobado: {mto.aprobado ? 'Sí' : 'No'}</Text>
      </View>
    </View>

    {/* Realiza */}
    <View style={styles.personnelHalf}>
      <View style={styles.signatureContainer}>
        {mto.firmaIngeniero
          ? <Image src={mto.firmaIngeniero} style={styles.signatureImage} />
          : <Text style={styles.noSignatureText}>Pendiente firma</Text>}
      </View>
      <View style={styles.personnelInfo}>
        <Text style={styles.labelPersonnel}>{mto.nombreIngeniero || 'N/A'}</Text>
        <Text style={styles.roleText}>Responsable Mantenimiento</Text>
      </View>
    </View>
  </View>
</View>

        {/* ===== FOOTER ===== */}
        <View style={styles.footer} fixed>
          <Image src={alphalogo} style={styles.footerLogo} />
          <Text style={styles.footerWeb}>www.alphabiomedica.com</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ReporteMtoCorrInfraPDF;
